import { useState, useEffect, useCallback } from 'react';
import { useRouter } from 'next/router';
import moment from 'moment-timezone';
import { TIMEZONE, INITIAL_FORM_DATA } from '../../lib/constants/bookingForm';

/**
 * Custom hook for managing booking form data and state
 */
export const useBookingFormData = () => {
    const router = useRouter();
    const now = moment().tz(TIMEZONE);
    const todayDate = now.format('YYYY-MM-DD');

    const [formData, setFormData] = useState(INITIAL_FORM_DATA);
    const [errors, setErrors] = useState({});
    const [isClicked, setIsClicked] = useState(false);
    const [timeError, setTimeError] = useState(false);

    // Check for URL parameters and update form data
    useEffect(() => {
        if (router.isReady) {
            const { gclid, utm_source, utm_medium, utm_campaign, utm_term, utm_content } = router.query;

            // Update form data with URL parameters
            const updates = {};

            if (gclid) {
                updates.gclid = gclid;
            }

            if (utm_source || utm_medium || utm_campaign || utm_term || utm_content) {
                updates.utm_source = utm_source || '';
                updates.utm_medium = utm_medium || '';
                updates.utm_campaign = utm_campaign || '';
                updates.utm_term = utm_term || '';
                updates.utm_content = utm_content || '';
            }

            // Update form data if any parameters were found
            if (Object.keys(updates).length > 0) {
                setFormData(prev => ({
                    ...prev,
                    ...updates,
                }));
            }
        }
    }, [router.isReady, router.query]);

    const handleInputChange = useCallback((field, value) => {
        setFormData(prev => {
            const newData = {
                ...prev,
                [field]: value,
            };

            // Clear return date and time when switching to One Way
            if (field === 'tripType' && value === '1') {
                newData.returnDate = '';
                newData.returnTime = '';
            }

            return newData;
        });

        // Clear error when user starts typing
        if (errors[field]) {
            setErrors(prev => ({
                ...prev,
                [field]: '',
            }));
        }

        // Real-time time validation for current date
        if ((field === 'departureTime' || field === 'returnTime') && value) {
            const dateField = field === 'departureTime' ? 'departureDate' : 'returnDate';
            const currentDate = formData[dateField];
            
            if (currentDate === todayDate) {
                const currentTime = moment().tz(TIMEZONE);
                const time24h = moment(value, 'hh:mm A').format('HH:mm');
                const selectedDateTime = moment(currentDate + ' ' + time24h, 'YYYY-MM-DD HH:mm');
                
                if (selectedDateTime.isBefore(currentTime)) {
                    setErrors(prev => ({
                        ...prev,
                        [field]: 'Please select a time from now onwards for today.',
                    }));
                } else {
                    // Only clear error if it's the specific time error
                    if (errors[field] === 'Please select a time from now onwards for today.') {
                        setErrors(prev => ({
                            ...prev,
                            [field]: '',
                        }));
                    }
                }
            }
        }
    }, [errors, formData, todayDate]);

    const handleDateChange = useCallback((field, value) => {
        // Always update the form data first to allow typing
        handleInputChange(field, value);

        // Only validate if it's a complete date (YYYY-MM-DD format)
        if (field === 'departureDate' || field === 'returnDate') {
            if (value && value.length === 10) { // Complete date format
                const selectedDate = moment(value, 'YYYY-MM-DD', true); // Strict parsing
                const today = moment().tz(TIMEZONE);
                
                // Check if it's a valid date
                if (!selectedDate.isValid()) {
                    setErrors(prev => ({
                        ...prev,
                        [field]: 'Please enter a valid date.',
                    }));
                    return;
                }
                
                // Check if selected date is before today
                if (selectedDate.isBefore(today, 'day')) {
                    setErrors(prev => ({
                        ...prev,
                        [field]: 'Please select a date from today onwards.',
                    }));
                    return;
                }

                // Additional validation for return date
                if (field === 'returnDate' && formData.departureDate) {
                    const departureDate = moment(formData.departureDate, 'YYYY-MM-DD', true);
                    if (selectedDate.isBefore(departureDate, 'day')) {
                        setErrors(prev => ({
                            ...prev,
                            [field]: 'Return date must be on or after departure date.',
                        }));
                        return;
                    }
                }

                // Clear any existing errors if validation passes
                if (errors[field]) {
                    setErrors(prev => ({
                        ...prev,
                        [field]: '',
                    }));
                }
            } else if (value && value.length > 0) {
                // Clear errors while typing incomplete dates
                if (errors[field]) {
                    setErrors(prev => ({
                        ...prev,
                        [field]: '',
                    }));
                }
            }
        }
    }, [handleInputChange, formData.departureDate, errors]);

    const setDefaultTime = useCallback((field) => {
        if (field === 'departureTime') {
            setFormData(prev => ({
                ...prev,
                departureTime: now.format('hh:mm A'),
            }));
        } else if (field === 'returnTime') {
            setFormData(prev => ({
                ...prev,
                returnTime: now.add(2, 'hours').format('hh:mm A'),
            }));
        }
    }, [now]);

    const handlePlaceSelect = useCallback((field, place) => {
        if (place && place.value) {
            const updates = {
                [field]: place.value.description,
            };

            // Extract coordinates if available
            if (place.value.geometry && place.value.geometry.location) {
                const lat = place.value.geometry.location.lat();
                const lng = place.value.geometry.location.lng();

                if (field === 'pickUpAddress') {
                    updates.pickupLat = lat;
                    updates.pickupLong = lng;
                } else if (field === 'dropOffAddress') {
                    updates.dropOffLat = lat;
                    updates.dropOffLong = lng;
                }
            } else {
                // Clear coordinates if no geometry data
                if (field === 'pickUpAddress') {
                    updates.pickupLat = null;
                    updates.pickupLong = null;
                } else if (field === 'dropOffAddress') {
                    updates.dropOffLat = null;
                    updates.dropOffLong = null;
                }
            }

            // Immediately update form data
            setFormData(prev => ({
                ...prev,
                ...updates,
            }));
        } else {
            // Handle when place is cleared
            const updates = {
                [field]: '',
            };

            // Clear coordinates when location is cleared
            if (field === 'pickUpAddress') {
                updates.pickupLat = null;
                updates.pickupLong = null;
            } else if (field === 'dropOffAddress') {
                updates.dropOffLat = null;
                updates.dropOffLong = null;
            }

            setFormData(prev => ({
                ...prev,
                ...updates,
            }));
        }
    }, []);

    const handleErrorChange = useCallback((field, errorType, errorMessage) => {
        const errorKey = `${field}_${errorType}`;
        setErrors(prev => ({
            ...prev,
            [errorKey]: errorMessage,
        }));
    }, []);

    return {
        formData,
        setFormData,
        errors,
        setErrors,
        isClicked,
        setIsClicked,
        timeError,
        setTimeError,
        todayDate,
        now,
        handleInputChange,
        handleDateChange,
        setDefaultTime,
        handlePlaceSelect,
        handleErrorChange,
    };
};
